import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Button, Card, Link } from '@sunrun/component-library';
import { Color } from '@sunrun/mui-theme';
import StyledLibraryLink from './StyledLibraryLink';
import { MAX_DESKTOP_BREAKPOINT, MIN_DESKTOP_BREAKPOINT } from './constants/_breakpoints';
import { OFF_WHITE_20 } from './constants/_colors';

const StyledCard = styled(Card)`
  display: flex;
  justify-content: space-between;
  box-sizing: border-box;
  margin-bottom: ${props => (props.extraMargin ? '64px !important' : '15px')};
  overflow: hidden;
  min-height: 166px;

  @media only screen and (max-width: ${MAX_DESKTOP_BREAKPOINT}) {
    width: 100%;
  }
`;

const TextContainer = styled.div`
  align-self: center;
  margin-right: 10px;
  padding: 0 20px;
  width: ${props => (props.hasSecondaryChildren ? 'calc(100% - 200px)' : '100%')};

  font-family: Roobert;
  font-size: 14px;
  font-weight: 400;

  @media only screen and (min-width: ${MIN_DESKTOP_BREAKPOINT}) {
    width: ${props => (props.hasChildren ? 'calc(100% - 200px)' : '100%')};
  }

  p {
    margin-top: 0;
    color: ${OFF_WHITE_20};
  }
`;

const StyledButton = styled(Button)`
  cursor: pointer;
  width: 100%;
`;

const StyledChildren = styled.div`
  width: 150px;

  @media only screen and (max-width: ${MAX_DESKTOP_BREAKPOINT}) {
    display: none;
  }
`;

const SecondaryChildren = styled.div`
  display: none;

  @media only screen and (max-width: ${MAX_DESKTOP_BREAKPOINT}) {
    display: block;
    width: 150px;
  }
`;

const StyledHeaderText = styled.div`
  font-size: 18px;
  font-family: Roobert;
  font-weight: 500;
  margin-bottom: 5px;
  color: ${Color.BLACK};
`;

const ContentCard = ({ button, children, header, link, secondaryChildren, text, extraMargin }) => (
  <StyledCard extraMargin={extraMargin}>
    <TextContainer hasChildren={children} hasSecondaryChildren={secondaryChildren}>
      <StyledHeaderText>{header}</StyledHeaderText>
      <p>
        {text && `${text} `}
        {link.text && (
          <StyledLibraryLink target={link.target} href={link.href} onClick={link.onClick} to={link.to}>
            {link.text}
          </StyledLibraryLink>
        )}
      </p>
      {button.text && button.href && (
        <Link to={button.href}>
          <StyledButton primary onClick={button.onClick}>
            {button.text}
          </StyledButton>
        </Link>
      )}
    </TextContainer>
    <StyledChildren>{children}</StyledChildren>
    {secondaryChildren && <SecondaryChildren>{secondaryChildren}</SecondaryChildren>}
  </StyledCard>
);

ContentCard.propTypes = {
  button: PropTypes.shape({
    text: PropTypes.string,
    href: PropTypes.string,
  }),
  children: PropTypes.element,
  header: PropTypes.string,
  link: PropTypes.shape({
    href: PropTypes.string,
    target: PropTypes.string,
    text: PropTypes.string,
  }),
  secondaryChildren: PropTypes.element,
  text: PropTypes.string,
  extraMargin: PropTypes.bool,
};

ContentCard.defaultProps = {
  button: {
    href: '',
    text: '',
  },
  children: <div />,
  header: '',
  link: {
    href: '',
    target: '',
    text: '',
  },
  secondaryChildren: null,
  text: '',
  extraMargin: false,
};

export default ContentCard;
